import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  AlertCircle,
  BarChart3,
  BriefcaseBusiness,
  CalendarDays,
  Check,
  CheckCircle,
  CheckCircle2,
  CircleDollarSign,
  Clock,
  Clock3,
  ExternalLink,
  Eye,
  Instagram,
  Mail,
  Search,
  Sparkles,
  UserRoundPlus,
  Wallet,
  X,
} from 'lucide-react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { useAuth } from '../auth/AuthContext';
import api, { getCreatorInvitations, getMyRecipientInvitations, respondToInvitation } from '../../services/api';
import { beginInstagramConnect } from '../../utils/instagramConnect';
import { Button, Card, Badge, CollabTypeBadge } from '../common/design-system';
import Avatar from '../common/Avatar';
import ImageWithFallback from '../common/ImageWithFallback';
import { getDefaultRouteForUser } from '../../utils/onboardingStatus';
import './InvitedCreatorDashboard.scss';

const isAvailabilityClosed = (invite) => (
  ['pending', 'opened'].includes(invite?.status)
    && ['full', 'cancelled', 'expired'].includes(invite?.offer_details?.availability_status)
);

const formatApplicationDate = (value, language) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString(language || 'es', { day: 'numeric', month: 'short', year: 'numeric' });
};

const formatApplicationDateRange = (start, end, language) => {
  const startLabel = formatApplicationDate(start, language);
  const endLabel = formatApplicationDate(end, language);
  if (startLabel && endLabel) return `${startLabel} – ${endLabel}`;
  return startLabel || endLabel;
};

// ─── Individual Invited Collab Card Component ────────────────
const InvitedCard = ({
  invite,
  onRespond,
  isResponding,
  isClosed,
  t,
  i18n,
  navigate,
}) => {
  const offer = invite.offer_details || {};
  const isAccepted = invite.status === 'accepted';
  const isPending = ['pending', 'opened'].includes(invite.status) && !isClosed;
  const isCancelled = offer.status === 'cancelled' || invite.status === 'cancelled';

  const statusConfig = useMemo(() => {
    if (isClosed) {
      return {
        label: isCancelled
          ? t('invited_dashboard.status_cancelled', 'Cancelada')
          : t('invited_dashboard.not_available', 'No disponible'),
        tone: 'error',
      };
    }
    if (isAccepted) {
      return { label: t('invited_dashboard.invitation_status.accepted', 'Aceptada'), tone: 'success' };
    }
    if (isPending) {
      return { label: t('invited_dashboard.invitation_status.pending', 'Pendiente'), tone: 'warning' };
    }
    if (invite.status === 'declined') {
      return { label: t('invited_dashboard.invitation_status.declined', 'Rechazada'), tone: 'error' };
    }
    return { label: invite.status || '', tone: 'neutral' };
  }, [isClosed, isCancelled, isAccepted, isPending, invite.status, t]);

  const scheduleLabel = formatApplicationDateRange(
    offer.from_date || offer.start_date,
    offer.to_date || offer.end_date,
    i18n.resolvedLanguage || i18n.language,
  );

  const compensationValue = Number(offer.cash_compensation) > 0
    ? `${offer.cash_currency || 'USD'} ${Number(offer.cash_compensation).toLocaleString()}`
    : Number(offer.exchange_value ?? offer.value ?? 0) > 0
      ? `${offer.cash_currency || 'USD'} ${Number(offer.exchange_value ?? offer.value ?? 0).toLocaleString()}`
      : null;

  const handleCardClick = (e) => {
    if (e.target.closest('button') || e.target.closest('a')) return;
    if (isAccepted) {
      navigate(`/creator/collabs/${invite.offer}`);
    } else {
      navigate(`/creator/offers/${invite.offer}`);
    }
  };

  const handleCardKeyDown = (e) => {
    if (e.target.closest('button') || e.target.closest('a')) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleCardClick(e);
    }
  };

  return (
    <Card
      className={clsx('app-card-editorial', isPending && 'is-pending', isAccepted && 'is-accepted')}
      interactive
      role="link"
      tabIndex={0}
      onClick={handleCardClick}
      onKeyDown={handleCardKeyDown}
      aria-label={`${offer.title || 'Colaboración'} — ${statusConfig.label}`}
    >
      <div className="card-header-visual">
        <ImageWithFallback
          src={offer.featured_image_url || offer.featured_image}
          alt={offer.title || 'Colaboración'}
          name={offer.title || 'Colaboración'}
          className="cover-img"
        />
        <div className="header-overlay">
          <div className="header-top-row">
            <div className="status-badge-group">
              <span className={clsx('status-badge', statusConfig.tone)}>
                <span className="status-dot" />
                {statusConfig.label}
              </span>
              <CollabTypeBadge offer={offer} size="sm" />
            </div>
            <div className="brand-abbr">
              <Avatar
                size="xs"
                src={offer.business_logo_url || offer.business_logo}
                name={offer.business_name}
                alt={`${offer.business_name || 'Negocio'} logo`}
              />
            </div>
          </div>
        </div>

        <div className="image-strip">
          <div className="strip-item">
            <CalendarDays size={12} />
            <span>{scheduleLabel || t('applications.card.upcoming', 'Próximamente')}</span>
          </div>
          {compensationValue && (
            <div className="strip-item">
              <CircleDollarSign size={12} />
              <span>{compensationValue}</span>
            </div>
          )}
        </div>
      </div>

      <div className="card-body-mid">
        <div className="brand-tag">{offer.business_name}</div>
        <h3>{offer.title}</h3>
        <p>
          {offer.description?.replace(/<[^>]*>/g, '').substring(0, 95)
            || t('applications.card.no_description', 'Colaboración privada exclusiva.')}
        </p>
        <div className="meta-grid">
          <div className="meta-pill">
            <Sparkles size={13} />
            <span>{offer.type_display || t('applications.defaults.general', 'Colaboración')}</span>
          </div>
          {Number(offer.cash_compensation) > 0 && (
            <div className="meta-pill gold">
              <CircleDollarSign size={13} />
              <span>{offer.cash_currency || 'USD'} {Number(offer.cash_compensation).toLocaleString()}</span>
            </div>
          )}
        </div>
      </div>

      <div className="card-footer-actions">
        {isPending ? (
          <>
            <Button
              variant="outline"
              size="md"
              className="card-action-button"
              onClick={(e) => {
                e.stopPropagation();
                onRespond(invite.id, 'decline', invite.offer);
              }}
              disabled={isResponding}
              style={{ flex: 1 }}
            >
              <X size={15} className="mr-1.5" />
              {t('applications.card.decline', 'Rechazar')}
            </Button>
            <Button
              variant="primary"
              size="md"
              className="card-action-button"
              onClick={(e) => {
                e.stopPropagation();
                onRespond(invite.id, 'accept', invite.offer);
              }}
              disabled={isResponding}
              style={{ flex: 1 }}
            >
              <Check size={15} className="mr-1.5" />
              {t('applications.card.accept', 'Aceptar')}
            </Button>
          </>
        ) : isAccepted ? (
          <>
            <Button
              variant="outline"
              size="md"
              className="card-action-button"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/creator/offers/${invite.offer}`);
              }}
              style={{ flex: 1 }}
            >
              <Eye size={15} className="mr-1.5" />
              {t('applications.card.view_details', 'Detalles')}
            </Button>
            <Button
              variant="primary"
              size="md"
              className="card-action-button"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/creator/collabs/${invite.offer}`);
              }}
              style={{ flex: 1 }}
            >
              <BarChart3 size={15} className="mr-1.5" />
              {t('applications.card.execution_details', 'Entregables')}
            </Button>
          </>
        ) : (
          <Button
            variant="outline"
            size="md"
            fullWidth
            className="card-action-button"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/creator/offers/${invite.offer}`);
            }}
          >
            <Eye size={15} className="mr-1.5" />
            {t('applications.card.view_offer_details', 'Ver oferta')}
          </Button>
        )}
      </div>
    </Card>
  );
};

// ─── Main InvitedCreatorDashboard Component ───────────────────
const InvitedCreatorDashboard = () => {
  const { t, i18n } = useTranslation('creator');
  const { collabId } = useParams();
  const { currentUser, refreshUser } = useAuth();
  const navigate = useNavigate();

  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [respondingId, setRespondingId] = useState(null);
  const [hubFilter, setHubFilter] = useState('all');
  const [hubQuery, setHubQuery] = useState('');

  const profile = currentUser?.creator_profile;
  const igConnected = profile?.instagram_account_connected;
  const isApprovedCreator = profile?.admin_verified && profile?.verification_status === 'approved';

  useEffect(() => {
    if (!currentUser) return;

    if (currentUser.user_type === 'business') {
      if (collabId) {
        navigate(`/business/collabs/${collabId}/journey`, { replace: true });
      } else {
        navigate('/business/collabs', { replace: true });
      }
      return;
    }

    if (currentUser.user_type !== 'creator') {
      navigate(getDefaultRouteForUser(currentUser), { replace: true });
      return;
    }

    if (isApprovedCreator) {
      navigate('/creator/feed', { replace: true });
    }
  }, [currentUser, isApprovedCreator, collabId, navigate]);

  const loadInvitations = useCallback(async () => {
    if (!currentUser || currentUser.user_type !== 'creator') {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const [recipientRes, directRes] = await Promise.allSettled([
        getMyRecipientInvitations(),
        getCreatorInvitations(),
      ]);
      const recipientRows = recipientRes.status === 'fulfilled' && Array.isArray(recipientRes.value?.data)
        ? recipientRes.value.data.map((r) => ({ ...r, source: r.source || 'recipient' }))
        : [];
      const directRows = directRes.status === 'fulfilled' && Array.isArray(directRes.value?.data)
        ? directRes.value.data.map((d) => ({ ...d, source: d.source || 'direct' }))
        : [];

      // Merge and deduplicate by offer id if needed
      const merged = [...recipientRows, ...directRows];
      setInvitations(merged);
    } catch (err) {
      console.error('Error loading invitations:', err);
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    loadInvitations();
  }, [loadInvitations]);

  // Statistics calculation
  const acceptedInvitations = useMemo(
    () => invitations.filter((invite) => invite.status === 'accepted'),
    [invitations],
  );

  const pendingInvitations = useMemo(
    () => invitations.filter((invite) => (
      ['pending', 'opened'].includes(invite.status) && !isAvailabilityClosed(invite)
    )),
    [invitations],
  );

  // Active / Most recent active invitation identification for the Hero
  const activeCollab = useMemo(() => {
    if (invitations.length === 0) return null;

    // 1. First priority: active accepted collaboration (creator needs to fulfill deliverables)
    const accepted = invitations.filter((invite) => invite.status === 'accepted');
    if (accepted.length > 0) {
      if (collabId && accepted.some((invite) => String(invite.offer) === String(collabId))) {
        return accepted.find((invite) => String(invite.offer) === String(collabId));
      }
      return [...accepted].sort((a, b) => (
        new Date(b.updated_at || b.created_at || 0) - new Date(a.updated_at || a.created_at || 0)
      ))[0];
    }

    // 2. Second priority: most recent pending invitation awaiting response
    const pending = invitations.filter((invite) => (
      ['pending', 'opened'].includes(invite.status) && !isAvailabilityClosed(invite)
    ));
    if (pending.length > 0) {
      if (collabId && pending.some((invite) => String(invite.offer) === String(collabId))) {
        return pending.find((invite) => String(invite.offer) === String(collabId));
      }
      return [...pending].sort((a, b) => (
        new Date(b.created_at || 0) - new Date(a.created_at || 0)
      ))[0];
    }

    // 3. Fallback: match URL param if available, otherwise first non-cancelled
    if (collabId && invitations.some((invite) => String(invite.offer) === String(collabId))) {
      return invitations.find((invite) => String(invite.offer) === String(collabId));
    }
    const nonCancelled = invitations.filter((invite) => (
      !isAvailabilityClosed(invite) && invite.offer_details?.status !== 'cancelled'
    ));
    return nonCancelled[0] || invitations[0] || null;
  }, [invitations, collabId]);

  // Filtered invitations list based on tab and query
  const visibleInvitations = useMemo(() => {
    const query = hubQuery.trim().toLocaleLowerCase();
    return invitations.filter((invite) => {
      const closed = isAvailabilityClosed(invite);
      const isCancelled = invite.offer_details?.status === 'cancelled' || invite.status === 'cancelled';
      const status = closed ? 'unavailable' : (isCancelled ? 'cancelled' : invite.status);

      const matchesFilter = hubFilter === 'all'
        || (hubFilter === 'pending' && ['pending', 'opened'].includes(status))
        || (hubFilter === 'accepted' && status === 'accepted')
        || (hubFilter === 'declined' && ['declined', 'cancelled'].includes(status));

      const title = invite.offer_details?.title || '';
      const business = invite.offer_details?.business_name || '';
      const matchesQuery = !query || `${title} ${business}`.toLocaleLowerCase().includes(query);

      return matchesFilter && matchesQuery;
    });
  }, [hubFilter, hubQuery, invitations]);

  // Respond to invitation handler (Accept / Decline)
  const handleInvitationResponse = async (id, action, offerId) => {
    try {
      setRespondingId(id);
      const targetInvite = invitations.find((inv) => inv.id === id);

      if (targetInvite?.source === 'recipient' && action === 'accept') {
        await api.post('/collabs/accept-recipient-invite/', {
          invitation_id: id,
          offer_id: offerId || targetInvite.offer,
        });
      } else {
        await respondToInvitation(id, action);
      }

      await refreshUser();

      toast.success(
        action === 'accept'
          ? t('applications.messages.invitation_accepted', '¡Invitación aceptada con éxito!')
          : t('applications.messages.invitation_declined', 'Invitación declinada'),
      );

      if (action === 'accept') {
        const targetOfferId = offerId || targetInvite?.offer;
        if (targetOfferId) {
          navigate(`/creator/collabs/${targetOfferId}`);
          return;
        }
      }

      await loadInvitations();
    } catch (error) {
      console.error('Error responding to invitation:', error);
      toast.error(t('applications.messages.invitation_error', 'Error al procesar la invitación'));
    } finally {
      setRespondingId(null);
    }
  };

  const handleInstagramConnect = () => {
    const onboardingTarget = activeCollab?.offer
      ? `/creator/offers/${activeCollab.offer}`
      : '/creator/invited';
    beginInstagramConnect(onboardingTarget);
  };

  if (!currentUser || currentUser.user_type !== 'creator') {
    return null;
  }

  if (loading) {
    return (
      <div className="invited-dashboard">
        <div className="loading-state">FOTH</div>
      </div>
    );
  }

  const activeOffer = activeCollab?.offer_details || {};
  const activeIsAccepted = activeCollab?.status === 'accepted';
  const activeDeadline = activeOffer.to_date
    ? formatApplicationDate(activeOffer.to_date, i18n.resolvedLanguage || i18n.language)
    : null;

  return (
    <div className="invited-dashboard invited-workspace">
      {/* ─── HERO SECTION: Title, Overview Stats & Active Collab Spotlight ─── */}
      <motion.section
        className="workspace-hero"
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div className="hero-glow" />
        <div className="hero-layout">
          {/* Left: Intro & Overview Stats */}
          <div className="hero-content">
            <div className="hero-badge">
              <Sparkles size={12} />
              <span>{t('invited_dashboard.workspace_eyebrow', 'Invitaciones privadas')}</span>
            </div>
            <div className="title-row">
              <div className="t-icon">
                <BriefcaseBusiness size={22} />
              </div>
              <h1>{t('invited_dashboard.workspace_title', 'Tu espacio de colaboraciones invitadas')}</h1>
            </div>
            <p className="lead">
              {t('invited_dashboard.workspace_subtitle', 'Revisa las oportunidades que marcas seleccionaron especialmente para ti.')}
            </p>

            <div className="stats-strip">
              <div className="stat-card-modern">
                <div className="stat-content">
                  <div className="icon-box">
                    <Mail size={22} />
                  </div>
                  <div className="stat-text-box">
                    <span className="stat-label">{t('invited_dashboard.hub_received', 'Invitaciones recibidas')}</span>
                    <strong className="stat-value">{invitations.length}</strong>
                  </div>
                </div>
              </div>

              <div className="stat-card-modern">
                <div className="stat-content">
                  <div className={clsx('icon-box', pendingInvitations.length > 0 && 'accent')}>
                    <Clock3 size={22} />
                  </div>
                  <div className="stat-text-box">
                    <span className="stat-label">{t('invited_dashboard.hub_pending', 'Pendientes de respuesta')}</span>
                    <strong className="stat-value">{pendingInvitations.length}</strong>
                  </div>
                </div>
              </div>

              <div className="stat-card-modern">
                <div className="stat-content">
                  <div className={clsx('icon-box', acceptedInvitations.length > 0 && 'accent-success')}>
                    <CheckCircle2 size={22} />
                  </div>
                  <div className="stat-text-box">
                    <span className="stat-label">{t('invited_dashboard.hub_accepted', 'Colaboraciones aceptadas')}</span>
                    <strong className="stat-value">{acceptedInvitations.length}</strong>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right: Active / Most Recent Invitation Spotlight */}
          <div className="hero-spotlight">
            {activeCollab ? (
              <div className={clsx('spotlight-card', activeIsAccepted ? 'is-active-collab' : 'is-pending-invite')}>
                <div className="spotlight-header">
                  <div className="spotlight-badge-wrap">
                    {activeIsAccepted ? (
                      <span className="spotlight-status-badge status-accepted">
                        <CheckCircle size={13} />
                        <span>{t('invited_dashboard.spotlight_active', 'Colaboración activa')}</span>
                      </span>
                    ) : (
                      <span className="spotlight-status-badge status-pending">
                        <Clock size={13} />
                        <span>{t('invited_dashboard.spotlight_pending', 'Invitación activa más reciente')}</span>
                      </span>
                    )}
                  </div>
                  <span className="spotlight-badge-label">
                    {t('invited_dashboard.spotlight_eyebrow', 'Destacada')}
                  </span>
                </div>

                <div className="spotlight-visual">
                  <ImageWithFallback
                    src={activeOffer.featured_image_url || activeOffer.featured_image}
                    alt={activeOffer.title}
                    name={activeOffer.title}
                    className="spotlight-cover-img"
                  />
                  <div className="spotlight-overlay">
                    <div className="spotlight-brand-pill">
                      <Avatar
                        size="xs"
                        src={activeOffer.business_logo_url || activeOffer.business_logo}
                        name={activeOffer.business_name}
                        alt={`${activeOffer.business_name || 'Negocio'} logo`}
                      />
                      <span>{activeOffer.business_name}</span>
                    </div>
                  </div>
                </div>

                <div className="spotlight-body">
                  <h3 className="spotlight-title">{activeOffer.title}</h3>
                  {activeOffer.description && (
                    <p className="spotlight-desc">
                      {activeOffer.description.replace(/<[^>]*>/g, '').substring(0, 110)}...
                    </p>
                  )}

                  <div className="spotlight-facts">
                    {(Number(activeOffer.cash_compensation) > 0 || Number(activeOffer.earnings) > 0) && (
                      <div className="fact-item fact-cash">
                        <span className="fact-label">{t('invited_dashboard.cash_label', 'Ganancia')}</span>
                        <strong className="fact-value">
                          <CircleDollarSign size={13} />
                          {activeOffer.cash_currency || 'USD'} {Number(activeOffer.cash_compensation || activeOffer.earnings || 0).toLocaleString()}
                        </strong>
                      </div>
                    )}
                    {Number(activeOffer.exchange_value ?? activeOffer.value ?? 0) > 0 && (
                      <div className="fact-item fact-exchange">
                        <span className="fact-label">{t('invited_dashboard.exchange_label', 'Experiencia')}</span>
                        <strong className="fact-value">
                          {activeOffer.cash_currency || 'USD'} {Number(activeOffer.exchange_value ?? activeOffer.value ?? 0).toLocaleString()}
                        </strong>
                      </div>
                    )}
                    {activeDeadline && (
                      <div className="fact-item fact-deadline">
                        <span className="fact-label">{t('invited_dashboard.deadline_label', 'Fecha límite')}</span>
                        <strong className="fact-value">
                          <CalendarDays size={13} />
                          {activeDeadline}
                        </strong>
                      </div>
                    )}
                  </div>

                  {!igConnected && (
                    <div className="spotlight-ig-callout">
                      <div className="ig-callout-text">
                        <Instagram size={15} />
                        <span>{t('invited_dashboard.ig_title', 'Conecta Instagram para continuar')}</span>
                      </div>
                      <button type="button" className="btn-spotlight-ig" onClick={handleInstagramConnect}>
                        {t('invited_dashboard.ig_btn', 'Conectar')}
                      </button>
                    </div>
                  )}

                  <div className="spotlight-actions">
                    {activeIsAccepted ? (
                      <Button
                        variant="primary"
                        size="md"
                        fullWidth
                        onClick={() => navigate(`/creator/collabs/${activeCollab.offer}`)}
                        className="spotlight-btn-primary"
                      >
                        <ExternalLink size={15} className="mr-1.5" />
                        <span>{t('invited_dashboard.view_and_deliver', 'Ver detalles y entregas')}</span>
                      </Button>
                    ) : (
                      <div className="spotlight-action-row">
                        <Button
                          variant="outline"
                          size="md"
                          onClick={() => handleInvitationResponse(activeCollab.id, 'decline', activeCollab.offer)}
                          disabled={respondingId === activeCollab.id}
                          style={{ flex: 1 }}
                        >
                          <X size={15} className="mr-1.5" />
                          <span>{t('applications.card.decline', 'Rechazar')}</span>
                        </Button>
                        <Button
                          variant="primary"
                          size="md"
                          onClick={() => handleInvitationResponse(activeCollab.id, 'accept', activeCollab.offer)}
                          disabled={respondingId === activeCollab.id}
                          style={{ flex: 1 }}
                        >
                          <Check size={15} className="mr-1.5" />
                          <span>{t('applications.card.accept', 'Aceptar')}</span>
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="spotlight-empty-card">
                <div className="empty-icon-wrap">
                  <Mail size={32} />
                </div>
                <h3>{t('invited_dashboard.no_active_collab_title', 'Esperando colaboraciones')}</h3>
                <p>
                  {t('invited_dashboard.no_active_collab_body', 'Cuando una marca te invite a una colaboración privada, aparecerá destacada aquí para que puedas gestionarla.')}
                </p>
              </div>
            )}
          </div>
        </div>
      </motion.section>

      {/* ─── ONBOARDING & VERIFICATION BANNERS ─── */}
      {profile?.verification_status === 'submitted' ? (
        <motion.div
          className="invited-status-banner status-submitted"
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          role="status"
          aria-live="polite"
        >
          <div className="status-banner-top">
            <div className="status-banner-badge">
              <span className="status-pulse-dot" />
              <Clock size={15} />
              <span>{t('invited_dashboard.review_badge', 'En revisión (24-48h)')}</span>
            </div>
            <div className="status-banner-chips">
              <span className="status-chip chip-success">
                <CheckCircle size={13} />
                <span>{t('invited_dashboard.review_chip_active', 'Colaboración invitada activa')}</span>
              </span>
              <span className="status-chip chip-neutral">
                <Sparkles size={13} />
                <span>{t('invited_dashboard.review_chip_deliverables', 'Entregables y pagos habilitados')}</span>
              </span>
              <span className="status-chip chip-neutral">
                <Clock size={13} />
                <span>{t('invited_dashboard.review_chip_email', 'Te notificaremos por email')}</span>
              </span>
            </div>
          </div>

          <div className="status-banner-main">
            <div className="status-banner-copy">
              <h2>{t('invited_dashboard.review_title', 'Acceso completo a FOTH en revisión')}</h2>
              <p>{t('invited_dashboard.review_banner_body', 'Tu perfil de creador fue enviado con éxito. Mientras nuestro equipo revisa tu cuenta para darte acceso a toda la red abierta de marcas FOTH, tus colaboraciones invitadas están 100% activas y puedes enviar entregables sin esperar.')}</p>
            </div>

            <div className="status-banner-timeline" aria-label={t('invited_dashboard.journey.aria', 'Progreso de verificación')}>
              <div className="timeline-node is-completed">
                <div className="timeline-icon"><CheckCircle size={14} /></div>
                <div className="timeline-text">
                  <small>01</small>
                  <strong>{t('invited_dashboard.review_step_onboarding', 'Onboarding enviado')}</strong>
                </div>
              </div>
              <div className="timeline-connector is-completed" />
              <div className="timeline-node is-active">
                <div className="timeline-icon"><Clock size={14} /></div>
                <div className="timeline-text">
                  <small>02</small>
                  <strong>{t('invited_dashboard.review_step_review', 'Revisión FOTH (en curso)')}</strong>
                </div>
              </div>
              <div className="timeline-connector" />
              <div className="timeline-node is-upcoming">
                <div className="timeline-icon"><Sparkles size={14} /></div>
                <div className="timeline-text">
                  <small>03</small>
                  <strong>{t('invited_dashboard.review_step_open_feed', 'Catálogo abierto')}</strong>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      ) : profile?.verification_status === 'rejected' ? (
        <motion.div
          className="invited-status-banner status-rejected"
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          role="status"
        >
          <div className="status-banner-top">
            <div className="status-banner-badge">
              <AlertCircle size={15} />
              <span>{t('invited_dashboard.curated_rejected_badge', 'Red curada no disponible')}</span>
            </div>
            <div className="status-banner-chips">
              <span className="status-chip chip-success">
                <CheckCircle size={13} />
                <span>{t('invited_dashboard.review_chip_active', 'Colaboración invitada activa')}</span>
              </span>
            </div>
          </div>
          <div className="status-banner-main">
            <div className="status-banner-copy">
              <h2>{t('invited_dashboard.curated_rejected_title', 'Acceso a red curada no disponible')}</h2>
              <p>{t('invited_dashboard.curated_rejected_body', 'Tu cuenta continúa activa para colaboraciones privadas invitadas directamente por marcas.')}</p>
            </div>
            <Link to="/complete-profile" className="btn-banner-cta">
              {t('invited_dashboard.reapply_cta', 'Actualizar perfil y volver a postular')}
            </Link>
          </div>
        </motion.div>
      ) : (
        <motion.div
          className="invited-status-banner status-unlock"
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          role="status"
        >
          <div className="status-banner-top">
            <div className="status-banner-badge">
              <UserRoundPlus size={15} />
              <span>{t('invited_dashboard.unlock_badge', 'Paso recomendado')}</span>
            </div>
          </div>
          <div className="status-banner-main">
            <div className="status-banner-copy">
              <h2>{t('invited_dashboard.unlock_title', 'Desbloquea más colaboraciones')}</h2>
              <p>{t('invited_dashboard.unlock_body', 'Completa tu onboarding para postularte a oportunidades abiertas, aparecer ante más marcas y construir tu perfil verificado.')}</p>
            </div>
            <Link to="/complete-profile" className="btn-banner-cta">
              <UserRoundPlus size={15} />
              {t('invited_dashboard.unlock_cta', 'Completar onboarding y aplicar')}
            </Link>
          </div>
        </motion.div>
      )}

      {/* ─── NAVIGATION TABS ─── */}
      <nav className="invited-dashboard-nav" aria-label={t('invited_dashboard.tab_collabs', 'Mis Collabs')}>
        <Link to="/creator/invited" className="nav-tab active">
          <BriefcaseBusiness size={16} />
          <span>{t('invited_dashboard.tab_collabs', 'Mis Collabs')}</span>
        </Link>
        <Link to="/creator/wallet" className="nav-tab">
          <Wallet size={16} />
          <span>{t('invited_dashboard.tab_earnings', 'Ganancias')}</span>
        </Link>
      </nav>

      {/* ─── CONTROLS BAR: Filter Tabs & Search ─── */}
      <div className="controls-bar">
        <div className="tabs-pill" role="tablist" aria-label={t('invited_dashboard.filter_aria', 'Filtrar invitaciones')}>
          <button
            type="button"
            className={clsx(hubFilter === 'all' && 'active')}
            onClick={() => setHubFilter('all')}
          >
            <span>{t('invited_dashboard.filters.all', 'Todas')}</span>
            <span className="badge-count">{invitations.length}</span>
          </button>
          <button
            type="button"
            className={clsx(hubFilter === 'pending' && 'active')}
            onClick={() => setHubFilter('pending')}
          >
            <Clock3 size={14} />
            <span>{t('invited_dashboard.filters.pending', 'Pendientes')}</span>
            {pendingInvitations.length > 0 && (
              <span className="badge-count">{pendingInvitations.length}</span>
            )}
          </button>
          <button
            type="button"
            className={clsx(hubFilter === 'accepted' && 'active')}
            onClick={() => setHubFilter('accepted')}
          >
            <CheckCircle2 size={14} />
            <span>{t('invited_dashboard.filters.accepted', 'Aceptadas')}</span>
            {acceptedInvitations.length > 0 && (
              <span className="badge-count">{acceptedInvitations.length}</span>
            )}
          </button>
          <button
            type="button"
            className={clsx(hubFilter === 'declined' && 'active')}
            onClick={() => setHubFilter('declined')}
          >
            <X size={14} />
            <span>{t('invited_dashboard.filters.declined', 'Rechazadas')}</span>
          </button>
        </div>

        <div className="search-wrapper">
          <Search size={16} aria-hidden="true" />
          <input
            type="search"
            value={hubQuery}
            onChange={(event) => setHubQuery(event.target.value)}
            placeholder={t('invited_dashboard.search_placeholder', 'Buscar negocio o colaboración')}
            aria-label={t('invited_dashboard.search_placeholder', 'Buscar negocio o colaboración')}
          />
        </div>
      </div>

      {/* ─── APPS GRID: Modern Editorial Collab Cards ─── */}
      <motion.div
        className="apps-grid"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.25 }}
      >
        <AnimatePresence mode="popLayout">
          {visibleInvitations.map((invite) => (
            <motion.div
              key={`${invite.source}-${invite.id}`}
              layout
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.2 }}
            >
              <InvitedCard
                invite={invite}
                onRespond={handleInvitationResponse}
                isResponding={respondingId === invite.id}
                isClosed={isAvailabilityClosed(invite)}
                t={t}
                i18n={i18n}
                navigate={navigate}
              />
            </motion.div>
          ))}
        </AnimatePresence>

        {visibleInvitations.length === 0 && (
          <div className="empty-st">
            <div className="empty-icon">
              <BriefcaseBusiness size={40} />
            </div>
            <h3>{t('applications.empty.no_results', 'No se encontraron colaboraciones')}</h3>
            <p>{t('applications.empty.no_results_help', 'No hay oportunidades que coincidan con los filtros seleccionados.')}</p>
            {hubFilter !== 'all' && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setHubFilter('all');
                  setHubQuery('');
                }}
              >
                {t('invited_dashboard.clear_filters', 'Ver todas las colaboraciones')}
              </Button>
            )}
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default InvitedCreatorDashboard;
